import { useState } from 'react';
import { AdminLayout } from '../components/Layout/AdminLayout';
import { PageHeader } from '../components/common';
import {
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Chip,
  InputAdornment,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Add, Edit, Delete, Search, Security } from '@mui/icons-material';
import { useRoles, useAllPermissions, usePermission } from '../hooks';
import { PermissionPicker } from '../components/common/PermissionPicker';
import { colors } from '../theme/theme';
import { useSnackbar } from '../contexts/SnackbarContext';

export const RolesPage = () => {
  const { can } = usePermission();
  const { showSnackbar } = useSnackbar();
  const [search, setSearch] = useState('');
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedRole, setSelectedRole] = useState<any>(null);
  const [formData, setFormData] = useState({ name: '', description: '', permissions: [] as number[] });

  const { roles, isLoading, error, createRole, updateRole, deleteRole, isCreating, isUpdating } = useRoles({ page: 1, per_page: 100 });
  const { permissions } = useAllPermissions();

  const allRoles: any[] = (() => {
    if (Array.isArray(roles)) return roles;
    if (roles?.data && Array.isArray(roles.data)) return roles.data;
    return [];
  })();

  const filteredRoles = allRoles.filter((role: any) =>
    !search ||
    role.name?.toLowerCase().includes(search.toLowerCase()) ||
    role.description?.toLowerCase().includes(search.toLowerCase())
  );

  const handleOpenDialog = (role?: any) => {
    if (role) {
      setSelectedRole(role);
      setFormData({
        name: role.name || '',
        description: role.description || '',
        permissions: role.permissions?.map((p: any) => p.id) || [],
      });
    } else {
      setSelectedRole(null);
      setFormData({ name: '', description: '', permissions: [] });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedRole(null);
    setFormData({ name: '', description: '', permissions: [] });
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      showSnackbar('Role name is required', 'error');
      return;
    }

    try {
      if (selectedRole) {
        await updateRole({
          id: selectedRole.id,
          data: { name: formData.name, description: formData.description, permissions: formData.permissions },
        });
        showSnackbar('Role updated successfully', 'success');
      } else {
        await createRole({ name: formData.name, description: formData.description, permission_ids: formData.permissions });
        showSnackbar('Role created successfully', 'success');
      }
      handleCloseDialog();
    } catch (err: any) {
      showSnackbar(err?.response?.data?.message || err?.message || 'Failed to save role', 'error');
    }
  };

  const handleDelete = async (id: number, name: string) => {
    if (name === 'County Admin') {
      showSnackbar('Cannot delete the default County Admin role', 'error');
      return;
    }
    if (!window.confirm(`Are you sure you want to delete the "${name}" role?`)) return;
    try {
      await deleteRole(id);
      showSnackbar('Role deleted successfully', 'success');
    } catch (err: any) {
      showSnackbar(err?.response?.data?.message || err?.message || 'Failed to delete role', 'error');
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Role Name',
      flex: 1,
      minWidth: 180,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Security sx={{ fontSize: 18, color: colors.primary.main }} />
          <Typography variant="body2" sx={{ fontWeight: 600, textTransform: 'capitalize' }}>
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 1,
      minWidth: 200,
      valueGetter: (value: any) => value || 'N/A',
    },
    {
      field: 'permissions',
      headerName: 'Permissions',
      flex: 1,
      minWidth: 250,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', py: 0.5 }}>
          {params.value?.slice(0, 3).map((p: any) => (
            <Chip key={p.id} label={p.name?.replace(/_/g, ' ')} size="small" variant="outlined" sx={{ textTransform: 'capitalize', fontSize: '0.7rem' }} />
          ))}
          {params.value?.length > 3 && (
            <Chip label={`+${params.value.length - 3}`} size="small" sx={{ bgcolor: colors.primary[50], color: colors.primary.main, fontWeight: 600 }} />
          )}
        </Box>
      ),
    },
    {
      field: 'users_count',
      headerName: 'Users',
      width: 80,
      align: 'center',
      headerAlign: 'center',
      valueGetter: (value: any) => value || 0,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {can('roles.edit') && <Tooltip title="Edit Role">
            <IconButton size="small" onClick={() => handleOpenDialog(params.row)} sx={{ color: colors.primary.main }}>
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>}
          {can('roles.delete') && params.row.name !== 'County Admin' && (
            <Tooltip title="Delete Role">
              <IconButton size="small" onClick={() => handleDelete(params.row.id, params.row.name)} sx={{ color: '#dc2626' }}>
                <Delete fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  return (
    <AdminLayout>
      <Box>
        <PageHeader
          title="Roles & Permissions"
          subtitle="Manage roles and assign permissions"
          breadcrumbs={[
            { label: 'Dashboard', path: '/dashboard' },
            { label: 'Roles' },
          ]}
          action={
            can('roles.create') ? <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenDialog()}>
              Add Role
            </Button> : undefined
          }
        />

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            Failed to load roles. Please try again.
          </Alert>
        )}

        <Card>
          <CardContent sx={{ p: 0 }}>
            <Box sx={{ p: 3, pb: 2 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search roles..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </Box>

            {isLoading ? (
              <Box display="flex" justifyContent="center" py={8}>
                <CircularProgress />
              </Box>
            ) : (
              <Box sx={{ height: 600, width: '100%' }}>
                <DataGrid
                  rows={filteredRoles}
                  columns={columns}
                  paginationModel={paginationModel}
                  onPaginationModelChange={setPaginationModel}
                  pageSizeOptions={[10, 25, 50]}
                  disableRowSelectionOnClick
                  getRowHeight={() => 'auto'}
                  sx={{
                    border: 'none',
                    '& .MuiDataGrid-cell': {
                      borderBottom: `1px solid ${colors.grey[200]}`,
                      display: 'flex',
                      alignItems: 'center',
                    },
                    '& .MuiDataGrid-row': {
                      minHeight: '52px !important',
                    },
                    '& .MuiDataGrid-columnHeaders': {
                      backgroundColor: colors.grey[50],
                      borderBottom: `2px solid ${colors.grey[300]}`,
                    },
                  }}
                />
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Add/Edit Dialog */}
        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle>
            {selectedRole ? 'Edit Role' : 'Add Role'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <TextField
                fullWidth
                label="Role Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                disabled={selectedRole?.name === 'County Admin'}
                helperText={selectedRole?.name === 'County Admin' ? 'Default role name cannot be changed' : ''}
              />
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={2}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Permissions</Typography>
              <PermissionPicker
                permissions={permissions}
                selected={formData.permissions}
                onChange={(selected) => setFormData({ ...formData, permissions: selected })}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={isCreating || isUpdating}
            >
              {(isCreating || isUpdating) ? <CircularProgress size={20} /> : (selectedRole ? 'Update' : 'Create')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AdminLayout>
  );
};
